// src/App.tsx
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { theme } from './styles/theme';
import LandingPage from './pages/LandingPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { UserProvider } from './contexts/UserContext';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import AboutPage from '@/pages/info/AboutPage';
import ContactPage from '@/pages/info/ContactPage';
import PartnershipsPage from '@/pages/info/PartnershipsPage';
import StudentDashboard from './pages/student/Dashboard';
import TeacherDashboard from './pages/teacher/Dashboard';
import EmployerDashboard from './pages/organization/Dashboard';
import { ApplicationsPage } from './pages/student/ApplicationsPage';
import { ApplicationDetailPage } from './pages/student/ApplicationDetailPage';
import { ProfilePage } from './pages/student/ProfilePage';
import { VolunteerHoursPage } from './pages/student/VolunteerHoursPage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import StudentLayout from './layouts/StudentLayout';
import TeacherLayout from './layouts/TeacherLayout';
import EmployerLayout from './layouts/EmployerLayout';
import OpportunityDetailPage from './pages/student/OpportunityDetailPage';
import OpportunityListPage from './pages/student/OpportunityListPage';
import { ToastProvider } from '@/components/common/Toast';
import { getFeatureFlags } from './config/featureFlags';
import HowItWorksPage from '@/pages/info/HowItWorksPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30000,
    },
  },
});

const App = () => {
  const featureFlags = getFeatureFlags();

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <AuthProvider>
            <UserProvider>
              <ToastProvider>
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<LandingPage />} />
                    {featureFlags.enableAuth && (
                      <>
                        <Route path="/login" element={<LoginPage />} />
                        {featureFlags.enableRegistration && (
                          <Route path="/register" element={<RegisterPage />} />
                        )}
                      </>
                    )}
                    
                    {/* Info Pages */}
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/how-it-works" element={<HowItWorksPage />} />
                    <Route path="/partnerships" element={<PartnershipsPage />} />

                    {/* Protected Routes */}
                    {featureFlags.enableAuth && (
                      <>
                        {/* Student Routes */}
                        <Route
                          path="/student/*"
                          element={
                            <ProtectedRoute role="student">
                              <StudentLayout>
                                <Routes>
                                  <Route index element={<StudentDashboard />} />
                                  <Route path="profile" element={<ProfilePage />} />
                                  {featureFlags.enableVolunteerSearch && (
                                    <>
                                      <Route path="opportunities" element={<OpportunityListPage />} />
                                      <Route path="opportunities/:id" element={<OpportunityDetailPage />} />
                                      <Route path="applications" element={<ApplicationsPage />} />
                                      <Route path="applications/new/:id" element={<ApplicationDetailPage />} />
                                      <Route path="applications/:id" element={<ApplicationDetailPage />} />
                                    </>
                                  )}
                                  <Route path="hours" element={<VolunteerHoursPage />} />
                                </Routes>
                              </StudentLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Teacher Routes */}
                        {featureFlags.enableSchoolPortal && (
                          <Route
                            path="/teacher/*"
                            element={
                              <ProtectedRoute role="teacher">
                                <TeacherLayout>
                                  <Routes>
                                    <Route index element={<TeacherDashboard />} />
                                  </Routes>
                                </TeacherLayout>
                              </ProtectedRoute>
                            }
                          />
                        )}

                        {/* Employer Routes */}
                        {featureFlags.enableOrganizationPortal && (
                          <Route
                            path="/employer/*"
                            element={
                              <ProtectedRoute role="organization">
                                <EmployerLayout>
                                  <Routes>
                                    <Route index element={<EmployerDashboard />} />
                                  </Routes>
                                </EmployerLayout>
                              </ProtectedRoute>
                            }
                          />
                        )}
                      </>
                    )}
                  </Routes>
                </ThemeProvider>
              </ToastProvider>
            </UserProvider>
          </AuthProvider>
        </ErrorBoundary>
      </QueryClientProvider>
    </BrowserRouter>
  );
};

export default App;